import {memo, useEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import PropTypes from 'prop-types';
import {
    DndContext,
    closestCenter,
    PointerSensor,
    KeyboardSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
    arrayMove,
} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';
import {templateShape} from '../shapes';

const DeleteIcon = () => (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
        <polyline points="1,3 11,3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        <path d="M4,3 L4,2 Q4,1 5,1 L7,1 Q8,1 8,2 L8,3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M2.5,3 L3,10 Q3,11 4,11 L8,11 Q9,11 9,10 L9.5,3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
);

function SortableTemplateItem({item, templateId, onUpdate, onDelete}) {
    const {attributes, listeners, setNodeRef, transform, transition, isDragging} = useSortable({id: item.id});
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState('');
    const [menuOpen, setMenuOpen] = useState(false);
    const [dropdownPos, setDropdownPos] = useState({top: 0, right: 0});
    const menuRef = useRef(null);
    const btnRef = useRef(null);

    useEffect(() => {
        if (!menuOpen) return;
        function handleMouseDown(e) {
            if (menuRef.current && !menuRef.current.contains(e.target) &&
                btnRef.current && !btnRef.current.contains(e.target)) {
                setMenuOpen(false);
            }
        }
        document.addEventListener('mousedown', handleMouseDown);
        return () => document.removeEventListener('mousedown', handleMouseDown);
    }, [menuOpen]);

    function openMenu() {
        if (btnRef.current) {
            const rect = btnRef.current.getBoundingClientRect();
            setDropdownPos({top: rect.bottom + 4, right: window.innerWidth - rect.right});
        }
        setMenuOpen(o => !o);
    }

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 1 : undefined,
        opacity: isDragging ? 0.5 : 1,
    };

    function startEditing() {
        setDraft(item.text);
        setEditing(true);
    }

    function commit() {
        const text = draft.trim();
        if (text && text !== item.text) onUpdate(templateId, item.id, text);
        setEditing(false);
    }

    function handleKeyDown(e) {
        if (e.key === 'Enter') commit();
        if (e.key === 'Escape') setEditing(false);
    }

    return (
        <div ref={setNodeRef} style={style} className="template-item" {...listeners} {...attributes}>
            <span className="checklist-item-drag-handle" aria-hidden="true">
                <svg width="10" height="14" viewBox="0 0 10 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="3" cy="2.5" r="1.2" fill="currentColor"/>
                    <circle cx="7" cy="2.5" r="1.2" fill="currentColor"/>
                    <circle cx="3" cy="7" r="1.2" fill="currentColor"/>
                    <circle cx="7" cy="7" r="1.2" fill="currentColor"/>
                    <circle cx="3" cy="11.5" r="1.2" fill="currentColor"/>
                    <circle cx="7" cy="11.5" r="1.2" fill="currentColor"/>
                </svg>
            </span>
            {editing
                ? <input
                    className="checklist-item-title-input"
                    value={draft}
                    onChange={e => setDraft(e.target.value)}
                    onBlur={commit}
                    onKeyDown={handleKeyDown}
                    autoFocus
                  />
                : <span
                    className="checklist-item-label"
                    onClick={startEditing}
                    onKeyDown={e => e.key === 'Enter' && startEditing()}
                    role="button"
                    tabIndex={0}
                  >{item.text}</span>
            }
            <div className={`checklist-item-menu${menuOpen ? ' checklist-item-menu--open' : ''}`}>
                <button
                    ref={btnRef}
                    className="checklist-item-menu-btn"
                    onClick={openMenu}
                    aria-label="Template item actions"
                    aria-expanded={menuOpen}
                >
                    <svg width="3" height="12" viewBox="0 0 3 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="1.5" cy="2" r="1.4" fill="currentColor"/>
                        <circle cx="1.5" cy="6" r="1.4" fill="currentColor"/>
                        <circle cx="1.5" cy="10" r="1.4" fill="currentColor"/>
                    </svg>
                </button>
            </div>
            {menuOpen && createPortal(
                <div
                    ref={menuRef}
                    className="checklist-item-menu-dropdown"
                    role="menu"
                    style={{top: dropdownPos.top, right: dropdownPos.right}}
                >
                    <button
                        className="checklist-item-menu-item checklist-item-menu-item--danger"
                        onClick={() => { onDelete(templateId, item.id); setMenuOpen(false); }}
                        aria-label="Delete template item"
                        role="menuitem"
                    >
                        <DeleteIcon />
                        Delete
                    </button>
                </div>,
                document.querySelector('.App') || document.body
            )}
        </div>
    );
}

SortableTemplateItem.propTypes = {
    item: PropTypes.shape({id: PropTypes.string, text: PropTypes.string}).isRequired,
    templateId: PropTypes.string.isRequired,
    onUpdate: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
};

// TemplateCard: data is the summary (from list), items are managed via callbacks.
// onExpand(id) → returns full template with items (Promise)
const TemplateCard = memo(function TemplateCard({
    data,
    onDelete,
    onUse,
    onExpand,
    onAddItem,
    onUpdateItem,
    onReorderItems,
    onDeleteItem,
    isDragEnabled,
    dragListeners,
}) {
    const [items, setItems] = useState([]);
    const [itemCount, setItemCount] = useState(data.itemCount);
    const [loaded, setLoaded] = useState(false);
    const [newItemText, setNewItemText] = useState('');
    const [menuOpen, setMenuOpen] = useState(false);
    const [dropdownPos, setDropdownPos] = useState({top: 0, right: 0});
    const menuRef = useRef(null);
    const menuBtnRef = useRef(null);

    useEffect(() => {
        if (!menuOpen) return;
        function handleMouseDown(e) {
            if (menuRef.current && !menuRef.current.contains(e.target) &&
                menuBtnRef.current && !menuBtnRef.current.contains(e.target)) {
                setMenuOpen(false);
            }
        }
        document.addEventListener('mousedown', handleMouseDown);
        return () => document.removeEventListener('mousedown', handleMouseDown);
    }, [menuOpen]);

    useEffect(() => {
        let cancelled = false;
        onExpand(data.id).then(full => {
            if (cancelled) return;
            setItems(full.items || []);
            setItemCount(full.itemCount);
            setLoaded(true);
        });
        return () => { cancelled = true; };
    }, [data.id, onExpand]);

    function openMenu() {
        if (menuBtnRef.current) {
            const rect = menuBtnRef.current.getBoundingClientRect();
            setDropdownPos({top: rect.bottom + 4, right: window.innerWidth - rect.right});
        }
        setMenuOpen(o => !o);
    }

    const sensors = useSensors(
        useSensor(PointerSensor, {activationConstraint: {distance: 5}}),
        useSensor(KeyboardSensor, {coordinateGetter: sortableKeyboardCoordinates}),
    );

    function handleDragEnd(event) {
        const {active, over} = event;
        if (!over || active.id === over.id) return;
        const oldIndex = items.findIndex(i => i.id === active.id);
        const newIndex = items.findIndex(i => i.id === over.id);
        const newItems = arrayMove(items, oldIndex, newIndex);
        const afterId = newIndex > 0 ? newItems[newIndex - 1].id : null;
        setItems(newItems);
        onReorderItems(data.id, active.id, afterId);
    }

    async function handleAddItem() {
        const text = newItemText.trim();
        if (!text) return;
        const newItem = await onAddItem(data.id, text);
        if (newItem) {
            setItems(prev => [...prev, newItem]);
            setItemCount(c => c + 1);
        }
        setNewItemText('');
    }

    async function handleDeleteItem(templateId, itemId) {
        await onDeleteItem(templateId, itemId);
        setItems(prev => prev.filter(i => i.id !== itemId));
        setItemCount(c => c - 1);
    }

    async function handleUpdateItem(templateId, itemId, text) {
        const updated = await onUpdateItem(templateId, itemId, text);
        if (updated) {
            setItems(prev => prev.map(i => i.id === itemId ? updated : i));
        }
    }

    return (
        <div className="checklist template-card">
            <div className="checklist-sticky">
                <div className="checklist-header">
                    {isDragEnabled && (
                        <span className="checklist-drag-handle" {...(dragListeners || {})} aria-label="Drag to reorder template">
                            <svg width="10" height="14" viewBox="0 0 10 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <circle cx="3" cy="2.5" r="1.2" fill="currentColor"/>
                                <circle cx="7" cy="2.5" r="1.2" fill="currentColor"/>
                                <circle cx="3" cy="7" r="1.2" fill="currentColor"/>
                                <circle cx="7" cy="7" r="1.2" fill="currentColor"/>
                                <circle cx="3" cy="11.5" r="1.2" fill="currentColor"/>
                                <circle cx="7" cy="11.5" r="1.2" fill="currentColor"/>
                            </svg>
                        </span>
                    )}
                    <div className="template-card-header-text">
                        <h2>{data.name}</h2>
                        {data.description && (
                            <p className="template-card-description">{data.description}</p>
                        )}
                    </div>
                    <div className="checklist-header-actions">
                        <span className="template-card-count">{itemCount} item{itemCount !== 1 ? 's' : ''}</span>
                        <button
                            className="template-use-btn"
                            onClick={() => onUse(data.id)}
                            aria-label={`Use template ${data.name}`}
                        >
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <rect x="3" y="1" width="8" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.3"/>
                                <rect x="1" y="3" width="8" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.3" fill="var(--surface)"/>
                                <line x1="5" y1="6.5" x2="5" y2="9.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
                                <line x1="3.5" y1="8" x2="6.5" y2="8" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
                            </svg>
                        </button>
                        <button
                            ref={menuBtnRef}
                            className="checklist-header-menu-btn"
                            onClick={openMenu}
                            aria-label="Template settings"
                            aria-expanded={menuOpen}
                        >
                            <svg width="3" height="12" viewBox="0 0 3 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <circle cx="1.5" cy="2" r="1.4" fill="currentColor"/>
                                <circle cx="1.5" cy="6" r="1.4" fill="currentColor"/>
                                <circle cx="1.5" cy="10" r="1.4" fill="currentColor"/>
                            </svg>
                        </button>
                    </div>
                </div>
            {loaded && (
                <div className="checklist-add-item">
                    <div className="checklist-add-item-inner">
                        <input
                            type="text"
                            placeholder="Add item"
                            value={newItemText}
                            onChange={e => setNewItemText(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && handleAddItem()}
                            aria-label="New template item"
                        />
                        <button onClick={handleAddItem}>Add</button>
                    </div>
                </div>
            )}
            </div>

            {loaded && (
                <>
                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                        <SortableContext items={items.map(i => i.id)} strategy={verticalListSortingStrategy}>
                            {items.map(item => (
                                <SortableTemplateItem
                                    key={item.id}
                                    item={item}
                                    templateId={data.id}
                                    onUpdate={handleUpdateItem}
                                    onDelete={handleDeleteItem}
                                />
                            ))}
                        </SortableContext>
                    </DndContext>
                </>
            )}
            {menuOpen && createPortal(
                <div
                    ref={menuRef}
                    className="checklist-item-menu-dropdown"
                    role="menu"
                    style={{top: dropdownPos.top, right: dropdownPos.right}}
                >
                    <button
                        className="checklist-item-menu-item checklist-item-menu-item--danger"
                        onClick={() => { onDelete(data.id); setMenuOpen(false); }}
                        aria-label="Delete template"
                        role="menuitem"
                    >
                        <DeleteIcon />
                        Delete
                    </button>
                </div>,
                document.querySelector('.App') || document.body
            )}
        </div>
    );
});

TemplateCard.propTypes = {
    data: templateShape.isRequired,
    onDelete: PropTypes.func.isRequired,
    onUse: PropTypes.func.isRequired,
    onExpand: PropTypes.func.isRequired,
    onAddItem: PropTypes.func.isRequired,
    onUpdateItem: PropTypes.func.isRequired,
    onReorderItems: PropTypes.func.isRequired,
    onDeleteItem: PropTypes.func.isRequired,
    isDragEnabled: PropTypes.bool,
    dragListeners: PropTypes.object,
};

export default TemplateCard;
